import {
  MinusCircleOutlined,
  PlusCircleFilled,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import { Calendar1Icon } from "lucide-react";
import { useMemo } from "react";
import { useStoreContext } from "../providers/store.provider";
import { IProductOnline } from "../types/types";
import { capitalize } from "../utils/functions";
import { toFixedDisplay } from "../utils/number.utils";

export function RowItem({
  key,
  onClick,
  item,
  productsToCheckout,
  onAddItems,
  onRemoveItems,
  fontColor,
}: {
  fontColor?: string;
  onRemoveItems: (product: IProductOnline) => void;
  productsToCheckout: IProductOnline[];
  item: IProductOnline;
  onAddItems: (product: IProductOnline) => void;
  key: number;
  onClick: (product: IProductOnline) => void;
}) {
  const { currency } = useStoreContext();
  const productCountMap: Map<string, number> = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of productsToCheckout) {
      const existing = map.get(p._id);
      if (existing) {
        map.set(p._id, existing + 1);
      } else {
        map.set(p._id, 1);
      }
    }
    return map;
  }, [productsToCheckout]);

  const count = productCountMap.get(item._id) ?? 0;
  const inCart = count > 0;

  return (
    <div
      className="flex items-center gap-4 my-3 border rounded-xl px-4 py-3 transition hover:shadow-md"
      key={key}
      style={{ borderColor: "rgba(128,128,128,0.25)" }}
    >
      <img
        className="w-16 h-16 rounded-lg object-cover flex-shrink-0 cursor-pointer bg-gray-100"
        src={item.cover ?? "/assets/images/no-picture.svg"}
        alt={item.title}
        onClick={() => onClick(item)}
      />

      <div
        className="flex flex-1 flex-col gap-0.5 min-w-0 cursor-pointer"
        onClick={() => onClick(item)}
      >
        <span className="text-base font-semibold leading-tight">
          {capitalize(item.title)}
        </span>
        {item.description && (
          <span
            className="text-xs opacity-70 leading-snug"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.description}
          </span>
        )}
        <span className="text-sm font-semibold mt-1">
          {currency}
          {toFixedDisplay(item.price.value)}
        </span>
      </div>

      <div className="flex items-center flex-shrink-0">
        {inCart ? (
          <div
            className="flex items-center rounded-full border"
            style={{ borderColor: fontColor ?? "rgba(128,128,128,0.4)" }}
          >
            <Button
              aria-label="Remove one"
              icon={
                <MinusCircleOutlined
                  style={{ fontSize: 22, color: fontColor }}
                />
              }
              type="link"
              onClick={(e) => {
                e.preventDefault();
                onRemoveItems(item);
              }}
            />
            <span
              className="min-w-[1.5rem] text-center font-semibold"
              style={{ color: fontColor }}
            >
              {count}
            </span>
            <Button
              aria-label="Add one"
              icon={
                <PlusCircleOutlined
                  style={{ fontSize: 22, color: fontColor }}
                />
              }
              type="link"
              onClick={(e) => {
                e.preventDefault();
                onAddItems(item);
              }}
            />
          </div>
        ) : (
          <Button
            shape="round"
            icon={<PlusCircleFilled style={{ fontSize: 18 }} />}
            type="primary"
            onClick={(e) => {
              e.preventDefault();
              onAddItems(item);
            }}
          >
            Add
          </Button>
        )}
      </div>
    </div>
  );
}

export function RowItemForReservation({
  key,
  onClick,
  item,
  productsToCheckout,
}: {
  productsToCheckout: IProductOnline[];
  item: IProductOnline;
  key: number;
  onClick: (product: IProductOnline) => void;
}) {
  const { currency } = useStoreContext();

  return (
    <div
      className="flex justify-between my-4 border rounded-xl px-8 py-2"
      key={key}
      onClick={() => onClick(item)}
    >
      <div className="flex flex-row gap-4 justify-center items-center">
        <img
          className="w-12 h-12 rounded-xl object-cover"
          src={item.cover ?? "/assets/images/no-picture.svg"}
          alt={"img-cover" + key}
        />
      </div>
      <div
        className="flex flex-1 flex-col gap-2 py-2 items-center justify-center"
        onClick={() => onClick(item)}
      >
        <span className="text-md font-bold">{capitalize(item.title)}</span>
        <span className="text-sm">
          {currency}
          {toFixedDisplay(item.price.value)}
        </span>
      </div>

      <div className="items-center flex">
        <span
          className="bg-blue-500 flex px-2 pr-3 py-1 gap-2 rounded-md items-center"
          style={{ color: "white" }}
        >
          <Calendar1Icon size={18} />
          Reserve
        </span>
      </div>
    </div>
  );
}
